/* eslint-disable */
import { Element } from '@ckeditor/ckeditor5-engine';
import Node from '@ckeditor/ckeditor5-engine/src/model/node';
import { ElementDefinition, TagAttributesDefinition } from './Definition';

interface SchemaInterface {
  elements: ElementDefinition[];
  tagAttributes: TagAttributesDefinition[];
}

class Schema {
  constructor() {}

  /**
   * タグ属性の属性値を取得する。
   * @param element 要素。
   * @param tagAttributeName タグ属性名。
   * @param attributeName 属性名。
   * @returns 属性値。
   */
  getAttribute = (element: Element, tagAttributeName: string, attributeName: string): string | undefined => {
    const tagAttributes = element.getAttribute(tagAttributeName) as any;
    if (!tagAttributes) {
      return undefined;
    }
    if (!tagAttributes.attributes) {
      return undefined;
    }

    return tagAttributes.attributes[attributeName];
  };

  /**
   * バリデーション用の名前を取得する。
   * @param node 対象ノード。
   * @returns バリデーション用の名前。
   */
  getNameForValidation = (node: Node): string => {
    if (node.is('$text')) {
      return '$text';
    }
    if (node.is('model:rootElement')) {
      return 'root';
    }

    const element = node as Element;

    // divの場合は特殊処理が必要
    if (element.name === 'htmlDiv') {
      const sectionId = this.getAttribute(element, 'htmlDivAttributes', 'section_id');
      if (sectionId) {
        return 'div@section_id';
      }
      const hasTitle = this.hasClass(element, 'htmlDivAttributes', 'title');
      const hasContents = this.hasClass(element, 'htmlDivAttributes', 'contents');
      return 'div' + (hasTitle ? '.title' : '') + (hasContents ? '.contents' : '');
    }
    // それ以外は定義された名前を返す
    else {
      const definition = this.schema.elements.find((elementDefinition) => elementDefinition.model === element.name);
      if (definition) {
        return definition.view.name;
      }
    }

    return element.name;
  };

  /**
   * セクションのレベルを取得する。
   * @param element 要素。
   * @returns セクションのレベル。
   */
  getSectionLevel = (element: Element): '2' | '3' | '4' | '5' | undefined => {
    let current = element;
    while (current.parent && !current.parent.is('model:rootElement')) {
      current = current.parent as Element;
    }

    switch (current.name) {
      case 'l2Section':
        return '2';

      case 'l3Section':
        return '3';

      case 'l4Section':
        return '4';

      case 'l5Section':
        return '5';

      default:
        return undefined;
    }
  };

  /**
   * バリデーション用の子どもの名前の配列を取得する。
   * @param element 要素。
   * @returns バリデーション用の子どもの名前の配列。
   */
  getChildrenNamesForValidation = (element: Element): string[] => {
    const result = [] as string[];

    const iterator = element.getChildren();
    let child = iterator.next();
    while (!child.done) {
      result.push(this.getNameForValidation(child.value));
      child = iterator.next();
    }

    return result;
  };

  /**
   * タグ属性にクラス名が存在するかを返す。
   * @param element 要素。
   * @param tagAttributeName タグ属性名。
   * @param className クラス名。
   * @returns クラス名が存在するか。
   */
  hasClass = (element: Element, tagAttributeName: string, className: string): boolean => {
    const tagAttributes = element.getAttribute(tagAttributeName) as any;
    if (!tagAttributes) {
      return false;
    }
    if (!tagAttributes.classes) {
      return false;
    }
    if (!Array.isArray(tagAttributes.classes)) {
      return false;
    }

    return tagAttributes.classes.includes(className);
  };

  /**
   * root要素のバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateRoot = (element: Element): [boolean, string] => {
    const name = this.getNameForValidation(element);
    if (name !== 'root') {
      return [false, `要素名が不正です: ${name}`];
    }

    const childNames = this.getChildrenNamesForValidation(element);

    // 最初の子要素はsection.l2であることを確認する。
    if (childNames.length === 0 || childNames[0] !== 'section.l2') {
      return [false, 'root要素の最初の子要素がsection.l2ではありません。'];
    }

    // 子要素はsection.l2, section.l3, section.l4, section.l5, div.page-breakのみ許容する。
    const expectedChildNames = [/^section.l[2-5]$/, /^div\.page-break$/];
    const unexpectedChildNames = childNames.filter(
      (childName) => !expectedChildNames.reduce((acc, current) => acc || current.test(childName), false)
    );
    if (unexpectedChildNames.length > 0) {
      return [false, `root要素直下に予期しないノードが含まれています: ${unexpectedChildNames.join(', ')}`];
    }

    return [true, ''];
  };

  /**
   * PSのroot要素のバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validatePSRoot = (element: Element): [boolean, string] => {
    const name = this.getNameForValidation(element);
    if (name !== 'root') {
      return [false, `要素名が不正です: ${name}`];
    }

    return [true, ''];
  };

  /**
   * L?セクションのバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateLSection = (element: Element): [boolean, string] => {
    // 要素名がsection.l?であることを確認する。
    const name = this.getNameForValidation(element);
    const pattern = /^section\.l(\d)$/;
    const result = pattern.exec(name);
    if (!result) {
      return [false, `要素名が不正です: ${name}`];
    }
    const level = parseInt(result[1]);

    // sectionの並び順が正しいことを確認する。
    {
      const getPreviousLevel = (targetElement: Element): number | undefined => {
        let current = targetElement;
        while (current.previousSibling) {
          current = current.previousSibling as Element;
          const subName = this.getNameForValidation(current);
          const subResult = pattern.exec(subName);
          if (subResult) {
            return parseInt(subResult[1]);
          }
        }
        return undefined;
      };

      const previousLevel = getPreviousLevel(element);
      if (previousLevel) {
        if (level - previousLevel > 1) {
          return [false, `section.l${previousLevel}の次にsection.l${level}を配置できません。`];
        }
      }
    }

    const childNames = this.getChildrenNamesForValidation(element);

    // 子要素は一つだけ許容する。
    if (childNames.length === 0) {
      return [false, `${name}要素の子要素が存在しません。`];
    }
    if (childNames.length > 1) {
      return [false, `${name}要素内に子要素が複数存在します: ${childNames.join(', ')}`];
    }

    // div.l?が存在することを確認する。
    const expectedChildName = `div.l${level}`;
    if (!childNames.includes(expectedChildName)) {
      return [false, `${name}要素内に子要素${expectedChildName}が存在しません: ${childNames.join(', ')}`];
    }

    return [true, ''];
  };

  /**
   * L?Textのバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateLText = (element: Element): [boolean, string] => {
    // 要素名がdiv.l?であることを確認する。
    const name = this.getNameForValidation(element);
    const pattern = /^div\.l(\d)$/;
    const result = pattern.exec(name);
    if (!result) {
      return [false, `要素名が不正です: ${name}`];
    }

    const childNames = this.getChildrenNamesForValidation(element);

    // 子要素は一つだけ許容する。
    if (childNames.length === 0) {
      return [false, `${name}要素の子要素が存在しません。`];
    }
    if (childNames.length > 1) {
      return [false, `${name}要素内に子要素が複数存在します: ${childNames.join(', ')}`];
    }

    // div@section_idが存在することを確認する。
    const expectedChildName = `div@section_id`;
    const unexpectedChildNames = childNames.filter((childName) => childName !== expectedChildName);
    if (unexpectedChildNames.length > 0) {
      return [
        false,
        `${name}要素直下に<div section_id="XXXXX">以外の要素が存在します: ${unexpectedChildNames.join(', ')}`,
      ];
    }

    return [true, ''];
  };

  /**
   * divのバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateHtmlDiv = (element: Element): [boolean, string] => {
    const name = this.getNameForValidation(element);
    const childNames = this.getChildrenNamesForValidation(element);

    const children = Array.from(element.getChildren());

    // section_id属性が存在するdivの場合
    if (name === 'div@section_id') {
      if (childNames.length === 0 || childNames.length > 2) {
        return [
          false,
          '<div section_id="XXXXX">直下に<div class="title">および<div class="contents">以外の要素が存在します。',
        ];
      }

      // 最初の子要素はdiv.titleであることを確認する。
      if (childNames[0] !== 'div.title') {
        return [false, '<div section_id="XXXXX">直下の先頭に<div class="title">以外の要素が存在します。'];
      }
      // 2番目の子要素はdiv.contentsであることを確認する。
      if (childNames.length > 1 && childNames[1] !== 'div.contents') {
        return [
          false,
          '<div section_id="XXXXX">直下に<div class="title">および<div class="contents">以外の要素が存在します。',
        ];
      }
      return [true, ''];
    }

    // div.contentsの場合はdiv@section_idの直下に配置されているかを確認する。
    if (name === 'div.contents' && element.parent) {
      const parentName = this.getNameForValidation(element.parent as Element);
      if (parentName !== 'div@section_id') {
        return [false, '<div class="contents">要素が<div section_id="XXXXX">の直下に配置されていません。'];
      }
    }

    // 通常のdivの場合の予期しているタグをセット
    let expectedChildNames = [
      /^p(\.[a-zA-Z_-]+)*$/,
      /^div(\.[a-zA-Z_-]+)*$/,
      /^h[1-5]$/,
      /^figure(\.[a-zA-Z_-]+)*$/,
      /^section(\.[a-zA-Z_-]+)*$/,
    ];

    // div.title配下の場合はh?（レベル）のみ許容する
    if (name === 'div.title') {
      const sectionLevel = this.getSectionLevel(element);
      if (sectionLevel) {
        expectedChildNames = [RegExp(`^h${sectionLevel}$`)];
      }
    }

    const unexpectedChildNames = childNames.filter(
      (childName) => !expectedChildNames.reduce((acc, current) => acc || current.test(childName), false)
    );

    if (unexpectedChildNames.length > 0) {
      return [false, `<${name}>直下に予期しないノードが含まれています: ${unexpectedChildNames.join(', ')}`];
    }

    return [true, ''];
  };

  /**
   * sectionタグのバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateHtmlSection = (element: Element): [boolean, string] => {
    const name = this.getNameForValidation(element);
    const childNames = this.getChildrenNamesForValidation(element);

    const expectedChildNames = [
      /^p(\.[a-zA-Z_-]+)*$/,
      /^a$/,
      /^figure(\.[a-zA-Z_-]+)*$/,
      /^table$/,
      /^div(\.[a-zA-Z_-]+)*$/,
      /^section(\.[a-zA-Z_-]+)*$/,
    ];

    const unexpectedChildNames = childNames.filter(
      (childName) => !expectedChildNames.reduce((acc, current) => acc || current.test(childName), false)
    );

    if (unexpectedChildNames.length > 0) {
      return [false, `<${name}>要素直下に予期しないノードが含まれています: ${unexpectedChildNames.join(', ')}`];
    }

    return [true, ''];
  };

  /**
   * tableタグのバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateTable = (element: Element): [boolean, string] => {
    const name = this.getNameForValidation(element);
    const childNames = this.getChildrenNamesForValidation(element);

    const expectedChildNames = [/^tr(\.[a-zA-Z_-]+)*$/];

    const unexpectedChildNames = childNames.filter(
      (childName) => !expectedChildNames.reduce((acc, current) => acc || current.test(childName), false)
    );

    if (unexpectedChildNames.length > 0) {
      return [false, `<${name}>要素直下に予期しないノードが含まれています: ${unexpectedChildNames.join(', ')}`];
    }

    return [true, ''];
  };

  /**
   * trタグのバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateTableRow = (element: Element): [boolean, string] => {
    const name = this.getNameForValidation(element);
    const childNames = this.getChildrenNamesForValidation(element);

    const expectedChildNames = [/^t[dh](\.[a-zA-Z_-]+)*$/];

    const unexpectedChildNames = childNames.filter(
      (childName) => !expectedChildNames.reduce((acc, current) => acc || current.test(childName), false)
    );

    if (unexpectedChildNames.length > 0) {
      return [false, `<${name}>要素直下に予期しないノードが含まれています: ${unexpectedChildNames.join(', ')}`];
    }

    return [true, ''];
  };

  /**
   * td/thタグのバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateTableCell = (element: Element): [boolean, string] => {
    const name = this.getNameForValidation(element);
    const childNames = this.getChildrenNamesForValidation(element);

    const expectedChildNames = [
      /^p(\.[a-zA-Z_-]+)*$/,
      /^div(\.[a-zA-Z_-]+)*$/,
      /^a$/,
      /^i$/,
      /^img$/,
      /^figure(\.[a-zA-Z_-]+)*$/,
      /^span(\.[a-zA-Z_-]+)*$/,
      /^strong$/,
      /^sup$/,
      /^sub$/,
      /^u$/,
      /^ruby$/,
    ];

    const unexpectedChildNames = childNames.filter(
      (childName) => !expectedChildNames.reduce((acc, current) => acc || current.test(childName), false)
    );

    if (unexpectedChildNames.length > 0) {
      return [false, `<${name}>要素直下に予期しないノードが含まれています: ${unexpectedChildNames.join(', ')}`];
    }

    return [true, ''];
  };

  /**
   * 要素が空であることを確認する。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateIsEmpty = (element: Element): [boolean, string] => {
    if (element.childCount > 0) {
      return [false, '子要素が存在します。'];
    }

    return [true, ''];
  };

  /**
   * タイトルヘッダ要素のバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateTitleHeader = (element: Element): [boolean, string] => {
    if (element.parent) {
      const parentName = this.getNameForValidation(element.parent as Element);

      // nameから要素名中のレベル番号を取得する
      const levelMatch = element.name.match(/^heading(\d)$/);
      if (!levelMatch) {
        return [false, '要素名が不正です'];
      }
      const level = Number(levelMatch[1]);

      // タイトル内のh?要素はid属性を持つ必要がある
      if (parentName === 'div.title') {
        // id属性が存在することを確認する
        const htmlAttributes = element.getAttribute(`htmlH${level}Attributes`) as any;
        if (!htmlAttributes?.attributes?.id) {
          return [false, 'id属性が存在しません。'];
        }
      }
      // h1からh4はdiv.title直下以外には配置できない
      else if (1 <= level && level <= 4) {
        const name = this.getNameForValidation(element);
        return [false, `<${name}>要素はdiv.title直下のみに配置できます。`];
      }
    }

    // インライン要素バリデーションを最後に実行する
    return this.validateInlineContainer(element);
  };

  /**
   * インライン要素のバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateInlineContainer = (element: Element): [boolean, string] => {
    const childNames = this.getChildrenNamesForValidation(element);

    const expectedChildNames = ['$text', 'img', 'a', 'strong', 'span', 'sub', 'sup', 'u', 'ruby', 'i'];
    const unexpectedChildNames = childNames
      .filter((childName) => !expectedChildNames.includes(childName))
      .filter((childName, index, self) => self.indexOf(childName) === index);

    if (unexpectedChildNames.length > 0) {
      if (unexpectedChildNames.length == 1 && unexpectedChildNames[0] === 'br') {
        return [false, `強制改行（br）が含まれています。`];
      } else {
        return [false, `インライン要素以外のノードが含まれています: ${unexpectedChildNames.join(', ')}`];
      }
    }

    return [true, ''];
  };

  /**
   * 画像のバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateImage = (element: Element): [boolean, string] => {
    // src属性が存在することを確認する
    const src = element.getAttribute('src');
    if (!src) {
      return [false, 'src属性が存在しません。'];
    }

    return this.validateIsEmpty(element);
  };

  /**
   * figureのバリデーションを行う。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validateHtmlFigure = (element: Element): [boolean, string] => {
    const name = this.getNameForValidation(element);
    const childNames = this.getChildrenNamesForValidation(element);

    const children = Array.from(element.getChildren());

    // 予期しているタグをセット
    let expectedChildNames = [
      /^p(\.[a-zA-Z_-]+)*$/,
      /^div(\.[a-zA-Z_-]+)*$/,
      /^h[1-5]$/,
      /^figure(\.[a-zA-Z_-]+)*$/,
      /^section(\.[a-zA-Z_-]+)*$/,
    ];

    const unexpectedChildNames = childNames.filter(
      (childName) => !expectedChildNames.reduce((acc, current) => acc || current.test(childName), false)
    );

    if (unexpectedChildNames.length > 0) {
      return [false, `<${name}>直下に予期しないノードが含まれています: ${unexpectedChildNames.join(', ')}`];
    }

    return [true, ''];
  };

  /**
   * バリデーションが未実装であることを示す。
   * @param element 要素。
   * @returns バリデーション結果。
   */
  validationNotImplemented = (element: Element): [boolean, string] => {
    return [false, '未実装です'];
  };

  schema: SchemaInterface = {
    elements: [
      {
        model: '$root',
        view: {
          display: (
            <>
              <strong>root</strong>
            </>
          ),
          name: 'root',
        },
        editableAttributes: [],
        validate: this.validateRoot,
      },
      {
        model: '$psroot',
        view: {
          display: (
            <>
              <strong>root</strong>
            </>
          ),
          name: 'root',
        },
        editableAttributes: [],
        validate: this.validatePSRoot,
      },
      {
        model: 'l2Section',
        view: {
          display: (
            <>
              <strong>section</strong>.l2
            </>
          ),
          name: 'section.l2',
        },
        editableAttributes: [],
        validate: this.validateLSection,
      },
      {
        model: 'l2Text',
        view: {
          display: (
            <>
              <strong>div</strong>.l2
            </>
          ),
          name: 'div.l2',
        },
        editableAttributes: [],
        validate: this.validateLText,
      },
      {
        model: 'l3Section',
        view: {
          display: (
            <>
              <strong>section</strong>.l3
            </>
          ),
          name: 'section.l3',
        },
        editableAttributes: [],
        validate: this.validateLSection,
      },
      {
        model: 'l3Text',
        view: {
          display: (
            <>
              <strong>div</strong>.l3
            </>
          ),
          name: 'div.l3',
        },
        editableAttributes: [],
        validate: this.validateLText,
      },
      {
        model: 'l4Section',
        view: {
          display: (
            <>
              <strong>section</strong>.l4
            </>
          ),
          name: 'section.l4',
        },
        editableAttributes: [],
        validate: this.validateLSection,
      },
      {
        model: 'l4Text',
        view: {
          display: (
            <>
              <strong>div</strong>.l4
            </>
          ),
          name: 'div.l4',
        },
        editableAttributes: [],
        validate: this.validateLText,
      },
      {
        model: 'l5Section',
        view: {
          display: (
            <>
              <strong>section</strong>.l5
            </>
          ),
          name: 'section.l5',
        },
        editableAttributes: [],
        validate: this.validateLSection,
      },
      {
        model: 'l5Text',
        view: {
          display: (
            <>
              <strong>div</strong>.l5
            </>
          ),
          name: 'div.l5',
        },
        editableAttributes: [],
        validate: this.validateLText,
      },
      {
        model: 'htmlDiv',
        view: {
          display: (
            <>
              <strong>div</strong>
            </>
          ),
          name: 'div',
        },
        editableAttributes: [{ name: 'htmlDivAttributes' }],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'htmlDiv',
        view: {
          display: (
            <>
              <strong>div</strong>
            </>
          ),
          name: 'div',
        },
        editableAttributes: [{ name: 'htmlDivAttributes' }],
        validate: this.validateHtmlDiv,
        optionForAttributes: '@section_id', // section_id属性を持つdiv
      },
      {
        model: 'htmlDiv',
        view: {
          display: (
            <>
              <strong>div</strong>
            </>
          ),
          name: 'div',
        },
        editableAttributes: [{ name: 'htmlDivAttributes' }],
        validate: this.validateHtmlDiv,
        optionForAttributes: '@ps', // ps系属性を持つdiv
      },
      {
        model: 'htmlDivParagraph',
        view: {
          display: (
            <>
              <strong>div</strong>
            </>
          ),
          name: 'div',
        },
        editableAttributes: [{ name: 'htmlDivAttributes' }],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'heading1',
        view: {
          display: (
            <>
              <strong>h1</strong>
            </>
          ),
          name: 'h1',
        },
        editableAttributes: [{ name: 'htmlH1Attributes' }],
        validate: this.validateTitleHeader,
      },
      {
        model: 'heading2',
        view: {
          display: (
            <>
              <strong>h2</strong>
            </>
          ),
          name: 'h2',
        },
        editableAttributes: [{ name: 'htmlH2Attributes' }],
        validate: this.validateTitleHeader,
      },
      {
        model: 'heading3',
        view: {
          display: (
            <>
              <strong>h3</strong>
            </>
          ),
          name: 'h3',
        },
        editableAttributes: [{ name: 'htmlH3Attributes' }],
        validate: this.validateTitleHeader,
      },
      {
        model: 'heading4',
        view: {
          display: (
            <>
              <strong>h4</strong>
            </>
          ),
          name: 'h4',
        },
        editableAttributes: [{ name: 'htmlH4Attributes' }],
        validate: this.validateTitleHeader,
      },
      {
        model: 'heading5',
        view: {
          display: (
            <>
              <strong>h5</strong>
            </>
          ),
          name: 'h5',
        },
        editableAttributes: [{ name: 'htmlH5Attributes' }],
        validate: this.validateTitleHeader,
      },
      {
        model: 'paragraph',
        view: {
          display: (
            <>
              <strong>p</strong>
            </>
          ),
          name: 'p',
        },
        editableAttributes: [
          { name: 'htmlUlAttributes', onlyExists: true },
          { name: 'htmlOlAttributes', onlyExists: true },
          { name: 'htmlLiAttributes', onlyExists: true },
          { name: 'htmlPAttributes' },
        ],
        validate: this.validateInlineContainer,
      },
      {
        model: 'paragraphRefer',
        view: {
          display: (
            <>
              <strong>p</strong>.refer
            </>
          ),
          name: 'p.refer',
        },
        editableAttributes: [{ name: 'htmlPAttributes' }],
        validate: this.validateInlineContainer,
      },
      {
        model: 'paragraphRefer1',
        view: {
          display: (
            <>
              <strong>p</strong>.refer
            </>
          ),
          name: 'p.refer',
        },
        editableAttributes: [{ name: 'htmlPAttributes' }],
        validate: this.validateInlineContainer,
      },
      {
        model: 'paragraphReferGear',
        view: {
          display: (
            <>
              <strong>p</strong>.refer.gear
            </>
          ),
          name: 'p.refer.gear',
        },
        editableAttributes: [{ name: 'htmlPAttributes' }],
        validate: this.validateInlineContainer,
      },
      {
        model: 'imageBlock',
        view: {
          display: (
            <>
              <strong>figure</strong>.image &gt; <strong>img</strong>
            </>
          ),
          name: 'figure.image',
        },
        editableAttributes: [{ name: 'htmlImgAttributes' }],
        isEmpty: true,
        validate: this.validateImage,
      },
      {
        model: 'imageInline',
        view: {
          display: (
            <>
              <strong>img</strong>
            </>
          ),
          name: 'img',
        },
        editableAttributes: [{ name: 'htmlImgAttributes' }],
        isEmpty: true,
        validate: this.validateImage,
      },
      {
        model: '$text',
        view: {
          display: <>(text)</>,
          name: '$text',
        },
        editableAttributes: [],
        validate: this.validationNotImplemented,
      },
      {
        model: 'danger',
        view: {
          display: (
            <>
              <strong>section</strong>.danger
            </>
          ),
          name: 'section.danger',
        },
        editableAttributes: [],
        validate: this.validateHtmlSection,
      },
      {
        model: 'dangerTitle',
        view: {
          display: (
            <>
              <strong>div</strong>.one-wrap
            </>
          ),
          name: 'div.one-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'warning',
        view: {
          display: (
            <>
              <strong>section</strong>.warning
            </>
          ),
          name: 'section.warning',
        },
        editableAttributes: [],
        validate: this.validateHtmlSection,
      },
      {
        model: 'warningTitle',
        view: {
          display: (
            <>
              <strong>div</strong>.one-wrap
            </>
          ),
          name: 'div.one-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'warningListTitle',
        view: {
          display: (
            <>
              <strong>p</strong>.warning_list_title
            </>
          ),
          name: 'p.warning_list_title',
        },
        editableAttributes: [{ name: 'htmlPAttributes' }],
        validate: this.validateInlineContainer,
      },
      {
        model: 'caution',
        view: {
          display: (
            <>
              <strong>section</strong>.caution
            </>
          ),
          name: 'section.caution',
        },
        editableAttributes: [],
        validate: this.validateHtmlSection,
      },
      {
        model: 'cautionTitle',
        view: {
          display: (
            <>
              <strong>div</strong>.one-wrap
            </>
          ),
          name: 'div.one-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'instructions',
        view: {
          display: (
            <>
              <strong>section</strong>.supplement
            </>
          ),
          name: 'section.supplement',
        },
        editableAttributes: [],
        validate: this.validateHtmlSection,
      },
      {
        model: 'instructionsTitle',
        view: {
          display: (
            <>
              <strong>div</strong>.one-wrap
            </>
          ),
          name: 'div.one-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'paragraphSupplementSubhead',
        view: {
          display: (
            <>
              <strong>p</strong>.supplement_subhead
            </>
          ),
          name: 'p.supplement_subhead',
        },
        editableAttributes: [{ name: 'htmlPAttributes' }],
        validate: this.validateInlineContainer,
      },
      {
        model: 'paragraphSupplementPara',
        view: {
          display: (
            <>
              <strong>p</strong>.supplement_para
            </>
          ),
          name: 'p.supplement_para',
        },
        editableAttributes: [{ name: 'htmlPAttributes' }],
        validate: this.validateInlineContainer,
      },

      {
        model: 'illustratedDanger',
        view: {
          display: (
            <>
              <strong>section</strong>.danger.cbox-inner
            </>
          ),
          name: 'section.danger.cbox-inner',
        },
        editableAttributes: [],
        validate: this.validateHtmlSection,
      },
      {
        model: 'illustratedDangerTitle',
        view: {
          display: (
            <>
              <strong>div</strong>.left-wrap
            </>
          ),
          name: 'div.left-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'illustratedDangerDescription',
        view: {
          display: (
            <>
              <strong>div</strong>.right-wrap
            </>
          ),
          name: 'div.right-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'illustratedWarning',
        view: {
          display: (
            <>
              <strong>section</strong>.warning.cbox-inner
            </>
          ),
          name: 'section.warning.cbox-inner',
        },
        editableAttributes: [],
        validate: this.validateHtmlSection,
      },
      {
        model: 'illustratedWarningTitle',
        view: {
          display: (
            <>
              <strong>div</strong>.left-wrap
            </>
          ),
          name: 'div.left-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'illustratedWarningDescription',
        view: {
          display: (
            <>
              <strong>div</strong>.right-wrap
            </>
          ),
          name: 'div.right-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'illustratedCaution',
        view: {
          display: (
            <>
              <strong>section</strong>.warning.cbox-inner
            </>
          ),
          name: 'section.warning.cbox-inner',
        },
        editableAttributes: [],
        validate: this.validateHtmlSection,
      },
      {
        model: 'illustratedCautionTitle',
        view: {
          display: (
            <>
              <strong>div</strong>.left-wrap
            </>
          ),
          name: 'div.left-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'illustratedCautionDescription',
        view: {
          display: (
            <>
              <strong>div</strong>.right-wrap
            </>
          ),
          name: 'div.right-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'illustedText',
        view: {
          display: (
            <>
              <strong>section</strong>.cbox-inner
            </>
          ),
          name: 'section.cbox-inner',
        },
        editableAttributes: [],
        validate: this.validateHtmlSection,
      },
      {
        model: 'illustedTextTitle',
        view: {
          display: (
            <>
              <strong>div</strong>.left-wrap
            </>
          ),
          name: 'div.left-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'illustedTextDescription',
        view: {
          display: (
            <>
              <strong>div</strong>.right-wrap
            </>
          ),
          name: 'div.right-wrap',
        },
        editableAttributes: [],
        validate: this.validateHtmlDiv,
      },
      {
        model: 'illustHeadline',
        view: {
          display: (
            <>
              <strong>p</strong>.imgtitle
            </>
          ),
          name: 'p.imgtitle',
        },
        editableAttributes: [],
        validate: this.validateInlineContainer,
      },
      {
        model: 'customGroup',
        view: {
          display: (
            <>
              <strong>p</strong>.custom_group
            </>
          ),
          name: 'p.custom_group',
        },
        editableAttributes: [],
        validate: this.validateInlineContainer,
      },
      {
        model: 'customItem',
        view: {
          display: (
            <>
              <strong>p</strong>.custom_item
            </>
          ),
          name: 'p.custom_item',
        },
        editableAttributes: [],
        validate: this.validateInlineContainer,
      },
      {
        model: 'pageBreak',
        view: {
          display: (
            <>
              <strong>div</strong>.page-break
            </>
          ),
          name: 'div.page-break',
        },
        isEmpty: true,
        editableAttributes: [],
        validate: this.validateIsEmpty,
      },
      {
        model: 'table',
        view: {
          display: (
            <>
              <strong>figure</strong>.table &gt; <strong>table</strong>
            </>
          ),
          name: 'figure.table',
        },
        editableAttributes: [{ name: 'htmlTableAttributes' }],
        validate: this.validateTable,
      },
      {
        model: 'tableRow',
        view: {
          display: (
            <>
              <strong>tr</strong>
            </>
          ),
          name: 'tr',
        },
        editableAttributes: [],
        validate: this.validateTableRow,
      },
      {
        model: 'tableCell',
        view: {
          display: (
            <>
              <strong>td</strong>
            </>
          ),
          name: 'td',
        },
        editableAttributes: [],
        validate: this.validateTableCell,
      },
      {
        model: 'softBreak',
        view: {
          display: (
            <>
              <strong>br</strong>
            </>
          ),
          name: 'br',
        },
        isEmpty: true,
        editableAttributes: [],
        validate: this.validateIsEmpty,
      },
      {
        model: 'htmlFigure',
        view: {
          display: (
            <>
              <strong>figure</strong>
            </>
          ),
          name: 'figure',
        },
        editableAttributes: [{ name: 'htmlFigureAttributes' }],
        validate: this.validateHtmlFigure,
      },
    ],
    tagAttributes: [
      {
        model: 'htmlUlAttributes',
        view: 'ul',
        editableAttributes: [],
      },
      {
        model: 'htmlOlAttributes',
        view: 'ol',
        editableAttributes: [],
      },
      {
        model: 'htmlLiAttributes',
        view: 'li',
        editableAttributes: [],
      },
      {
        model: 'htmlTableAttributes',
        view: 'table',
        editableAttributes: [],
      },
      {
        model: 'htmlTbodyAttributes',
        view: 'tbody',
      },
      {
        model: 'htmlThAttributes',
        view: 'th',
      },
      {
        model: 'htmlTdAttributes',
        view: 'td',
      },
      {
        model: 'htmlPAttributes',
        view: 'p',
        editableAttributes: ['line-height'],
      },
      {
        model: 'htmlDivAttributes',
        view: 'div',
        editableAttributes: ['font-stretch', 'line-height'],
      },
      {
        model: 'htmlDivAttributes',
        view: 'div',
        editableAttributes: ['pagetype'],
        optionForAttributes: '@section_id',
      },
      {
        model: 'htmlDivAttributes',
        view: 'div',
        editableAttributes: ['ps_no', 'ps_version_id', 'ps_derivation_id'],
        optionForAttributes: '@ps',
      },
      {
        model: 'htmlH1Attributes',
        view: 'h1',
        editableAttributes: ['line-height'],
      },
      {
        model: 'htmlH2Attributes',
        view: 'h2',
        editableAttributes: ['line-height'],
      },
      {
        model: 'htmlH3Attributes',
        view: 'h3',
        editableAttributes: ['line-height'],
      },
      {
        model: 'htmlH4Attributes',
        view: 'h4',
        editableAttributes: ['line-height'],
      },
      {
        model: 'htmlH5Attributes',
        view: 'h5',
        editableAttributes: ['line-height'],
      },
      {
        model: 'htmlFigureAttributes',
        view: 'figure',
        editableAttributes: [],
      },
      {
        model: 'htmlImgAttributes',
        view: 'img',
        editableAttributes: [],
      },
      {
        model: 'htmlAAttributes',
        view: 'a',
        editableAttributes: ['href'],
      },
      {
        model: 'htmlA',
        view: 'a',
        editableAttributes: ['href'],
      },
      {
        model: 'htmlSpanAttributes',
        view: 'span',
        editableAttributes: ['font-stretch'],
      },
      {
        model: 'htmlSpan',
        view: 'span',
        editableAttributes: ['font-stretch'],
      },
    ],
  };

  /**
   * 定義を返す。
   * @param modelName モデル名。
   * @returns 定義。
   */
  getElementDefinition(
    elementName: string,
    optionForAttributes: string | undefined = undefined
  ): ElementDefinition | undefined {
    if (optionForAttributes) {
      return this.schema.elements.find(
        (element) => element.model === elementName && element.optionForAttributes === optionForAttributes
      );
    } else {
      return this.schema.elements.find((element) => element.model === elementName);
    }
  }

  /**
   * 属性の定義を返す。
   * @param name 属性名。
   * @returns 属性定義。
   */
  getTagAttributesDefinition(
    name: string,
    optionForAttributes: string | undefined = undefined
  ): TagAttributesDefinition | undefined {
    if (optionForAttributes) {
      return this.schema.tagAttributes.find(
        (attributes) => attributes.model === name && attributes.optionForAttributes === optionForAttributes
      );
    } else {
      return this.schema.tagAttributes.find((attributes) => attributes.model === name);
    }
  }
}

export default new Schema();
